import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import StickyDataTable from "../../../components/StickyDataTable"
import { TAgetRunpodWorkers, TAstopRunpodWorkers } from "../../../services/runpodAPI"
import { columnConfig } from "./components/columns"
import RunpodPosts from "./components/RunpodPosts"
import CreateRunpodWorkerModal from "./components/CreateRunpodWorkerModal"

const Runpod = () => {
  const navigate = useNavigate()

  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]
  const [sortByColumn, setSortByColumn] = useState("")
  const [sortOrder, setSortOrder] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [data, setData] = useState([])
  const [totalDocuments, setTotalDocuments] = useState(0)
  const [getQuery, setGetQuery] = useState({
    start_date: "",
    end_date: "",
    page: 1,
    per_page: 10,
    is_active: null,
    is_running: null,
  })
  const [showPostsModal, setShowPostsModal] = useState(false)
  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [gpuIndex, setGpuIndex] = useState(0)

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null
    getRunpodWorkers()
    intervalId = setInterval(() => {
      getRunpodWorkers()
    }, 60000) // 1 dakika
    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getQuery])

  const getRunpodWorkers = async () => {
    setIsLoading(true)
    setError("")
    try {
      const response = await TAgetRunpodWorkers(getQuery)
      setData(response.data)
      setTotalDocuments(response.total)
      setIsLoading(false)
    } catch (error: unknown) {
      if (
        typeof error === "object" &&
        error !== null &&
        (("name" in error && (error as { name?: string }).name === "AbortError") ||
          ("code" in error && (error as { code?: string }).code === "ERR_CANCELED"))
      ) {
        setIsLoading(false)
        return
      }
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        (error as { response?: { status?: number } }).response?.status === 401
      ) {
        navigate("/login")
      }

      setError("Failed to fetch Runpod workers.")
      setIsLoading(false)
    }
  }

  const stopRunpodWorker = async (pod_id: string) => {
    setIsLoading(true)
    setError("")
    try {
      await TAstopRunpodWorkers(pod_id)
      await getRunpodWorkers()
      setIsLoading(false)
    } catch (error: unknown) {
      if (
        typeof error === "object" &&
        error !== null &&
        (("name" in error && (error as { name?: string }).name === "AbortError") ||
          ("code" in error && (error as { code?: string }).code === "ERR_CANCELED"))
      ) {
        setIsLoading(false)
        return
      }
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        (error as { response?: { status?: number } }).response?.status === 401
      ) {
        navigate("/login")
      }

      setError("Failed to fetch Runpod workers.")
      setIsLoading(false)
    }
  }

  const handleSorting = (columnAccessor: string, direction?: "asc" | "desc") => {
    if (columnAccessor === sortByColumn) {
      setSortOrder(direction === "desc")
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(direction === "desc")
    }
  }

  const handleShowPosts = (postIds: string[]) => {
    setSelectedPostIds(postIds)
    setShowPostsModal(true)
  }

  const columns = columnConfig({
    onShowPosts: handleShowPosts,
    stopRunpodWorker: stopRunpodWorker,
    setGpuIndex: setGpuIndex,
  })

  return (
    <div>
      {error && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
          role="alert"
        >
          {error}
        </div>
      )}
      <div className="flex justify-end mb-4">
        <button
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded shadow"
          onClick={() => setShowCreateModal(true)}
        >
          Runpod Worker Oluştur
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
        </div>
      ) : (
        <StickyDataTable
          records={data}
          columns={columns}
          totalRecords={totalDocuments}
          recordsPerPage={getQuery.per_page}
          page={getQuery.page}
          onPageChange={(p) => setGetQuery((prev) => ({ ...prev, page: p }))}
          recordsPerPageOptions={PAGE_SIZES}
          onRecordsPerPageChange={(size) => setGetQuery((prev) => ({ ...prev, per_page: size }))}
          sortStatus={{
            columnAccessor: "id",
            direction: "asc",
          }}
          onSortStatusChange={(e) => handleSorting(e.columnAccessor, e.direction)}
          minHeight={200}
          paginationText={({ from, to, totalRecords }) =>
            `Showing  ${from} to ${to} of ${totalRecords} entries`
          }
          highlightOnHover={true}
          className="whitespace-nowrap"
        />
      )}
      <RunpodPosts
        isOpen={showPostsModal}
        onClose={() => setShowPostsModal(false)}
        post_ids={selectedPostIds}
        gpuIndex={gpuIndex}
      />
      <CreateRunpodWorkerModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => {
          setShowCreateModal(false)
          getRunpodWorkers()
        }}
      />
    </div>
  )
}

export default Runpod
